import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useCopyLayout,
  useResetLayout,
  useSaveLayout,
  type ElementPlacement,
  type HudElementSpec,
  type LayoutPayload,
  type OverlayLayout,
} from '../api/queries';
import { boxOf, clampToFrame, prune, snap } from './geometry';
import type { Box } from './geometry';

/**
 * Состояние редактора раскладки - ОТДЕЛЬНО ОТ ТОГО, ЧТО ЕГО ПОКАЗЫВАЕТ.
 *
 * Вынесено из экрана `LayoutZone` 2026-09-19, когда редактор переехал в кадр
 * консоли и стал рисоваться поверх живого оверлея. Показ поменялся целиком,
 * механика - ни в одном месте: снап, направляющие, история, охранные зоны,
 * перенос раскладки между играми остались теми же и проверяются теми же
 * сценариями.
 *
 * Две реализации этой механики держать нельзя: расхождение в том, где стоит
 * элемент, вылезло бы не в консоли, а у зрителя.
 */
export interface LayoutEditor {
  layout: OverlayLayout;
  selected: string | null;
  select: (id: string | null) => void;
  guides: { vertical: number[]; horizontal: number[] };
  /** Прямоугольники всех видимых элементов, кроме выбранного. */
  others: Box[];
  canUndo: boolean;
  undo: () => void;
  /** Сдвиг выбранного элемента на dx, dy логических пикселей кадра. */
  move: (element: HudElementSpec, dx: number, dy: number, withSnap: boolean) => void;
  /** Точечная правка размещения: масштаб, растяжение, скрытие. */
  patch: (id: string, change: Partial<ElementPlacement>) => void;
  /** Вернуть ОДИН элемент на заводское место. */
  restore: (id: string) => void;
  /** Сбросить раскладку целиком. */
  resetAll: () => void;
  /** Перенести раскладку из другой игры. */
  copyFrom: (gameId: string) => void;
  resetPending: boolean;
  copyPending: boolean;
  /** Человеческое имя элемента из словаря игры. */
  label: (element: HudElementSpec) => string;
}

export function useLayoutEditor(
  gameId: string,
  data: LayoutPayload,
  /**
   * Позвать перемонтирование: раскладку целиком заменил СЕРВЕР - перенос из
   * другой игры или сброс. Только эти два случая, и только они: на каждое
   * сохранение перемонтировать нельзя, иначе после первого же шага
   * обнуляется история отмены.
   */
  onServerReplaced?: () => void,
): LayoutEditor {
  const save = useSaveLayout(gameId);
  const reset = useResetLayout(gameId);
  const copy = useCopyLayout(gameId);

  /*
   * Начальное состояние - то, что пришло с сервера. Дальше состояние местное:
   * каждое сохранение возвращает ту же раскладку, и подхватывать её обратно
   * незачем. Замена целиком - перенос и сброс - приходит перемонтированием
   * через `onServerReplaced`.
   */
  const [layout, setLayout] = useState<OverlayLayout>(data.layout);
  const [selected, setSelected] = useState<string | null>(null);
  const [guides, setGuides] = useState<{ vertical: number[]; horizontal: number[] }>({
    vertical: [],
    horizontal: [],
  });

  /**
   * История на десяток шагов. У эталона её нет вовсе: каждое перетаскивание
   * немедленно и необратимо, и вернуться можно только сбросив всю раскладку.
   *
   * Состоянием, а не ссылкой: глубину истории читает кнопка отмены при
   * отрисовке, а ссылку читать во время отрисовки нельзя.
   */
  const [history, setHistory] = useState<OverlayLayout[]>([]);

  const label = useCallback(
    (element: HudElementSpec): string =>
      element.labelKey === undefined
        ? element.id
        : (data.dictionary[element.labelKey] ?? element.labelKey),
    [data.dictionary],
  );

  const commit = useCallback(
    (next: OverlayLayout, remember = true) => {
      if (remember) {
        setHistory((stack) => [...stack, layout].slice(-10));
      }

      const cleaned = prune(next);
      setLayout(cleaned);
      save.mutate(cleaned);
    },
    [layout, save],
  );

  const undo = useCallback(() => {
    const previous = history.at(-1);

    if (previous === undefined) return;

    setHistory((stack) => stack.slice(0, -1));
    setLayout(previous);
    save.mutate(previous);
  }, [history, save]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        undo();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [undo]);

  const others = useMemo(
    () =>
      data.elements
        .filter((element) => element.id !== selected && layout[element.id]?.hidden !== true)
        .map((element) => boxOf(element, layout[element.id])),
    [data.elements, layout, selected],
  );

  const move = useCallback(
    (element: HudElementSpec, dx: number, dy: number, withSnap: boolean) => {
      const current = layout[element.id] ?? {};
      const moved: ElementPlacement = {
        ...current,
        x: (current.x ?? 0) + dx,
        y: (current.y ?? 0) + dy,
      };

      const box = boxOf(element, moved);
      const snapped = snap(box, others, withSnap);

      setGuides(snapped.guides);

      const placed = clampToFrame(element, {
        ...moved,
        x: snapped.x - element.x,
        y: snapped.y - element.y,
      });

      commit({ ...layout, [element.id]: placed });
    },
    [commit, layout, others],
  );

  const patch = useCallback(
    (id: string, change: Partial<ElementPlacement>) => {
      commit({ ...layout, [id]: { ...(layout[id] ?? {}), ...change } });
    },
    [commit, layout],
  );

  const restore = useCallback(
    (id: string) => {
      const next = { ...layout };
      delete next[id];
      commit(next);
    },
    [commit, layout],
  );

  const resetAll = useCallback(() => {
    setLayout({});
    reset.mutate(undefined, { onSuccess: () => onServerReplaced?.() });
  }, [onServerReplaced, reset]);

  const copyFrom = useCallback(
    (from: string) => {
      /* Перенос считает сервер: он знает, какие элементы у игр общие, а какие
         пропущены. Готовая раскладка приезжает перезапросом, поэтому после
         ответа редактор перемонтируется с новым началом. */
      copy.mutate(from, { onSuccess: () => onServerReplaced?.() });
    },
    [copy, onServerReplaced],
  );

  return {
    layout,
    selected,
    select: setSelected,
    guides,
    others,
    canUndo: history.length > 0,
    undo,
    move,
    patch,
    restore,
    resetAll,
    copyFrom,
    resetPending: reset.isPending,
    copyPending: copy.isPending,
    label,
  };
}
